import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConsultationPrep = () => {
  const [activeTab, setActiveTab] = useState('preparation');

  const preparationSteps = [
    {
      step: 1,
      title: 'Unterlagen sammeln',
      description: 'Sammeln Sie alle relevanten Dokumente zu Ihrem Fall',
      details: [
        'Verträge, Vereinbarungen oder Schriftstücke',
        'E-Mails oder andere Korrespondenz',
        'Rechnungen, Belege oder Kostenvoranschläge',
        'Fotos oder andere Beweismittel',
        'Bereits erhaltene Rechtsberatung'
      ],
      icon: 'FileText'
    },
    {
      step: 2,
      title: 'Fragen formulieren',
      description: 'Notieren Sie sich Ihre wichtigsten Fragen im Voraus',
      details: [
        'Was ist Ihr Hauptanliegen?',
        'Welche Rechte haben Sie in dieser Situation?',
        'Welche Handlungsoptionen stehen Ihnen offen?',
        'Mit welchen Kosten müssen Sie rechnen?',
        'Wie sind die Erfolgsaussichten?'
      ],
      icon: 'HelpCircle'
    },
    {
      step: 3,
      title: 'Chronologie erstellen',
      description: 'Erstellen Sie eine zeitliche Übersicht der Ereignisse',
      details: [
        'Wann ist das Problem entstanden?',
        'Welche Schritte haben Sie bereits unternommen?',
        'Gibt es wichtige Fristen zu beachten?',
        'Wer sind die beteiligten Parteien?',
        'Welche Kommunikation gab es bereits?'
      ],
      icon: 'Calendar'
    },
    {
      step: 4,
      title: 'Ziele definieren',
      description: 'Überlegen Sie sich, was Sie erreichen möchten',
      details: [
        'Was ist Ihr gewünschtes Ergebnis?',
        'Welche Kompromisse sind Sie bereit einzugehen?',
        'Wie wichtig ist Ihnen eine schnelle Lösung?',
        'Welches Budget steht zur Verfügung?',
        'Möchten Sie eine außergerichtliche Einigung?'
      ],
      icon: 'Target'
    }
  ];

  const consultationTypes = [
    {
      type: 'Sofortberatung',
      duration: '5-15 Minuten',
      bestFor: 'Schnelle Fragen, erste Einschätzung',
      preparation: 'Minimal',
      tips: [
        'Haben Sie Ihre wichtigste Frage bereit',
        'Halten Sie relevante Dokumente griffbereit',
        'Seien Sie präzise und konkret',
        'Notieren Sie sich die Antworten'
      ]
    },
    {
      type: 'Terminberatung',
      duration: '30-60 Minuten',
      bestFor: 'Komplexe Fälle, ausführliche Beratung',
      preparation: 'Umfassend',
      tips: [
        'Senden Sie Unterlagen vorab per E-Mail',
        'Bereiten Sie eine Fragenliste vor',
        'Planen Sie ausreichend Zeit ein',
        'Bringen Sie Notizmaterial mit'
      ]
    },
    {
      type: 'Schriftliche Anfrage',
      duration: '24h Bearbeitungszeit',
      bestFor: 'Dokumentierte Einschätzung',
      preparation: 'Detailliert',
      tips: [
        'Beschreiben Sie den Sachverhalt ausführlich',
        'Fügen Sie alle relevanten Dokumente bei',
        'Stellen Sie konkrete Fragen',
        'Geben Sie Ihre Kontaktdaten vollständig an'
      ]
    }
  ];

  const tabs = [
    { id: 'preparation', label: 'Vorbereitung', icon: 'CheckSquare' },
    { id: 'types', label: 'Beratungsarten', icon: 'MessageSquare' },
    { id: 'checklist', label: 'Checkliste', icon: 'List' }
  ];

  const downloadChecklist = () => {
    // Simulate checklist download
    alert('Checkliste wird heruntergeladen...');
  };

  return (
    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Optimal vorbereitet zur Rechtsberatung
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Eine gute Vorbereitung hilft Ihnen, das Maximum aus Ihrer Rechtsberatung herauszuholen 
            und Zeit sowie Kosten zu sparen.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center mb-12 bg-surface rounded-2xl p-2">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeTab === tab?.id
                  ? 'bg-white text-primary shadow-professional'
                  : 'text-text-secondary hover:text-primary hover:bg-white/50'
              }`}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>

        {/* Preparation Tab */}
        {activeTab === 'preparation' && (
          <div className="space-y-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {preparationSteps?.map((step) => (
                <div key={step?.step} className="bg-surface rounded-2xl p-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={step?.icon} size={24} color="white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="w-8 h-8 bg-conversion-accent text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {step?.step}
                        </span>
                        <h3 className="text-xl font-bold text-text-primary">
                          {step?.title}
                        </h3>
                      </div>
                      <p className="text-text-secondary mb-4">
                        {step?.description}
                      </p>
                      <ul className="space-y-2">
                        {step?.details?.map((detail, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Icon name="Check" size={14} color="var(--color-trust-builder)" className="mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-text-secondary">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consultation Types Tab */}
        {activeTab === 'types' && (
          <div className="grid lg:grid-cols-3 gap-8">
            {consultationTypes?.map((consultation, index) => (
              <div key={index} className="bg-surface rounded-2xl p-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-text-primary mb-2">
                    {consultation?.type}
                  </h3>
                  <div className="text-sm text-text-secondary space-y-1">
                    <p><strong>Dauer:</strong> {consultation?.duration}</p>
                    <p><strong>Ideal für:</strong> {consultation?.bestFor}</p>
                    <p><strong>Vorbereitung:</strong> {consultation?.preparation}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-text-primary mb-3">Tipps für diese Beratungsart:</h4>
                  <ul className="space-y-2">
                    {consultation?.tips?.map((tip, tipIndex) => (
                      <li key={tipIndex} className="flex items-start space-x-2">
                        <Icon name="Lightbulb" size={14} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-text-secondary">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-surface rounded-2xl p-8">
              <div className="text-center mb-8">
                <Icon name="Download" size={32} color="var(--color-primary)" className="mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-text-primary mb-2">
                  Beratungs-Checkliste herunterladen
                </h3>
                <p className="text-text-secondary">
                  Laden Sie unsere umfassende Checkliste herunter, um optimal vorbereitet zu sein.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="font-semibold text-text-primary mb-4">Die Checkliste enthält:</h4>
                  <ul className="space-y-2">
                    {[
                      'Vollständige Dokumentenliste',
                      'Fragenkatalog nach Rechtsgebieten',
                      'Zeitplan für die Vorbereitung',
                      'Tipps für das Beratungsgespräch',
                      'Nachbereitungs-Leitfaden'
                    ]?.map((item, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <Icon name="Check" size={16} color="var(--color-trust-builder)" />
                        <span className="text-sm text-text-secondary">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-text-primary mb-4">Verfügbare Formate:</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Icon name="FileText" size={20} color="var(--color-primary)" />
                        <span className="text-sm font-medium">PDF-Checkliste</span>
                      </div>
                      <Button variant="outline" size="sm">
                        Download
                      </Button>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Icon name="Smartphone" size={20} color="var(--color-primary)" />
                        <span className="text-sm font-medium">Mobile App</span>
                      </div>
                      <Button variant="outline" size="sm">
                        Bald verfügbar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Download"
                  iconPosition="left"
                  onClick={downloadChecklist}
                  className="bg-trust-builder hover:bg-trust-builder/90 text-white"
                >
                  Kostenlose Checkliste herunterladen
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Bereit für Ihre Rechtsberatung?
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Mit der richtigen Vorbereitung können Sie das Maximum aus Ihrer Beratung herausholen. 
              Starten Sie jetzt Ihre professionelle Rechtsberatung.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="bg-white text-primary hover:bg-white/90"
              >
                Sofort anrufen
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="border-white text-white hover:bg-white hover:text-primary"
              >
                Termin vereinbaren
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ConsultationPrep;